import { z } from 'zod';
import type { AccessLevel } from './constants';
import type { DocParentType } from './docs';

export const WHITEBOARD_TEMPLATES = ['blank', 'brainstorm', 'flowchart', 'kanban', 'retro'] as const;
export type WhiteboardTemplate = (typeof WHITEBOARD_TEMPLATES)[number];

export interface WhiteboardDTO {
  id: string;
  parentType: DocParentType;
  parentId: string;
  title: string;
  isPrivate: boolean;
  /** PNG preview as a data URL, when one has been saved. */
  thumbnail: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: number;
  updatedAt: number;
  access: AccessLevel;
}

export interface WhiteboardChangedEventData {
  whiteboardId: string;
  op: 'created' | 'updated' | 'deleted' | 'restored';
}

const id = z.string().min(1).max(64);
const parentType = z.enum(['workspace', 'space', 'folder', 'list']);

export const whiteboardQuerySchema = z.object({ parentType: parentType.optional(), parentId: id.optional() });

export const createWhiteboardSchema = z.object({
  parentType: parentType.default('workspace'),
  parentId: id.optional(),
  title: z.string().trim().max(200).optional(),
  isPrivate: z.boolean().optional(),
});
export type CreateWhiteboardInput = z.infer<typeof createWhiteboardSchema>;

export const updateWhiteboardSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  isPrivate: z.boolean().optional(),
  parentType: parentType.optional(),
  parentId: id.optional(),
  /** A small PNG data URL (at most about 200 KB) for the hub. */
  thumbnail: z.string().max(260_000).nullable().optional(),
});
export type UpdateWhiteboardInput = z.infer<typeof updateWhiteboardSchema>;

/**
 * Shape of the Yjs document behind a board, shared by the canvas and anything that reads boards.
 * `elements` is a Y.Map from element id to a Y.Map of the element's fields.
 */
export const BOARD_ELEMENTS = 'elements';
export const BOARD_META = 'meta';

export const ELEMENT_TYPES = ['sticky', 'rect', 'ellipse', 'diamond', 'triangle', 'text', 'connector', 'pen', 'highlight', 'image', 'frame', 'task'] as const;
export type ElementType = (typeof ELEMENT_TYPES)[number];

/** A connector endpoint: a free point, or a side of another element. */
export interface ConnectorEnd {
  x: number;
  y: number;
  elementId?: string;
  anchor?: 'top' | 'right' | 'bottom' | 'left';
}

export interface BoardElement {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
  /** Fractional index for z-order. */
  index: string;
  locked?: boolean;
  groupId?: string | null;
  /** Frame this element sits in, if any. */
  frameId?: string | null;
  text?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  fontSize?: number;
  /** pen and highlight: points relative to x, y. */
  points?: number[][];
  /** connector */
  from?: ConnectorEnd;
  to?: ConnectorEnd;
  arrowStart?: boolean;
  arrowEnd?: boolean;
  /** image */
  url?: string;
  /** task card */
  taskId?: string;
  [key: string]: unknown;
}
